import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useToast } from 'vue-toastification'
import { useProductsStore } from './products'

export const useCartStore = defineStore('cart', () => {
    // State
    const items = ref([])
    const deliveryType = ref('pickup') // pickup, delivery
    const deliveryDate = ref(null)
    const deliveryTime = ref(null)
    const deliveryAddress = ref(null)
    const customerNotes = ref('')
    const isCheckingOut = ref(false)

    const toast = useToast()
    const productsStore = useProductsStore()

    // Getters
    const itemCount = computed(() => {
        return items.value.reduce((total, item) => total + item.quantity, 0)
    })

    const subtotal = computed(() => {
        return items.value.reduce((total, item) => {
            const itemPrice = item.price + (item.variant?.price_delta || 0)
            return total + (itemPrice * item.quantity)
        }, 0)
    })

    const deliveryFee = computed(() => {
        return deliveryType.value === 'delivery' ? 5.00 : 0
    })

    const total = computed(() => {
        return subtotal.value + deliveryFee.value
    })

    const isEmpty = computed(() => items.value.length === 0)

    const cartItemsWithDetails = computed(() => {
        return items.value.map(item => {
            const product = productsStore.products.find(p => p.id === item.productId)
            return {
                ...item,
                product,
                totalPrice: (item.price + (item.variant?.price_delta || 0)) * item.quantity
            }
        })
    })

    // Actions
    const addItem = (product, variant = null, quantity = 1, customText = '') => {
        // Controlla se il prodotto è già nel carrello con la stessa variante
        const existingItemIndex = items.value.findIndex(item =>
            item.productId === product.id &&
            item.variantId === variant?.id &&
            item.customText === customText
        )

        if (existingItemIndex > -1) {
            // Se esiste, aumenta la quantità
            items.value[existingItemIndex].quantity += quantity
            toast.success(`Quantità aggiornata nel carrello`)
        } else {
            // Altrimenti aggiungi nuovo item
            items.value.push({
                id: Date.now() + Math.random(), // ID temporaneo unico
                productId: product.id,
                productName: product.name,
                variantId: variant?.id || null,
                variant: variant,
                price: product.price_base,
                quantity,
                customText,
                image: product.images?.[0] || null,
                addedAt: new Date().toISOString()
            })
            toast.success(`${product.name} aggiunto al carrello`)
        }

        saveToLocalStorage()
    }

    const updateQuantity = (itemId, quantity) => {
        const item = items.value.find(i => i.id === itemId)
        if (item) {
            if (quantity <= 0) {
                removeItem(itemId)
            } else {
                item.quantity = quantity
                saveToLocalStorage()
            }
        }
    }

    const removeItem = (itemId) => {
        const index = items.value.findIndex(i => i.id === itemId)
        if (index > -1) {
            const item = items.value[index]
            items.value.splice(index, 1)
            toast.info(`${item.productName} rimosso dal carrello`)
            saveToLocalStorage()
        }
    }

    const updateCustomText = (itemId, text) => {
        const item = items.value.find(i => i.id === itemId)
        if (item) {
            item.customText = text
            saveToLocalStorage()
        }
    }

    const clearCart = () => {
        items.value = []
        deliveryType.value = 'pickup'
        deliveryDate.value = null
        deliveryTime.value = null
        deliveryAddress.value = null
        customerNotes.value = ''
        saveToLocalStorage()
    }

    const setDeliveryInfo = (info) => {
        if (info.type) deliveryType.value = info.type
        if (info.date) deliveryDate.value = info.date
        if (info.time) deliveryTime.value = info.time
        if (info.address) deliveryAddress.value = info.address
        if (info.notes !== undefined) customerNotes.value = info.notes
        saveToLocalStorage()
    }

    const validateCart = () => {
        // Verifica che il carrello non sia vuoto
        if (isEmpty.value) {
            toast.error('Il carrello è vuoto')
            return false
        }

        // Verifica che tutti i prodotti siano ancora disponibili
        for (const item of items.value) {
            const product = productsStore.products.find(p => p.id === item.productId)
            if (!product || !product.active) {
                toast.error(`${item.productName} non è più disponibile`)
                removeItem(item.id)
                return false
            }
        }

        // Verifica informazioni di consegna
        if (!deliveryDate.value || !deliveryTime.value) {
            toast.error('Seleziona data e ora di consegna/ritiro')
            return false
        }

        if (deliveryType.value === 'delivery' && !deliveryAddress.value) {
            toast.error('Inserisci l\'indirizzo di consegna')
            return false
        }

        return true
    }

    const getOrderData = () => {
        if (!validateCart()) return null

        return {
            items: items.value.map(item => ({
                product_id: item.productId,
                variant_id: item.variantId,
                quantity: item.quantity,
                price: item.price + (item.variant?.price_delta || 0),
                custom_text: item.customText
            })),
            delivery_type: deliveryType.value,
            date_needed: deliveryDate.value,
            slot_time: deliveryTime.value,
            delivery_address: deliveryAddress.value,
            notes: customerNotes.value,
            subtotal: subtotal.value,
            delivery_fee: deliveryFee.value,
            total: total.value
        }
    }

    // Persistenza localStorage
    const saveToLocalStorage = () => {
        const cartData = {
            items: items.value,
            deliveryType: deliveryType.value,
            deliveryDate: deliveryDate.value,
            deliveryTime: deliveryTime.value,
            deliveryAddress: deliveryAddress.value,
            customerNotes: customerNotes.value
        }
        localStorage.setItem('cart', JSON.stringify(cartData))
    }

    const loadFromLocalStorage = () => {
        const savedCart = localStorage.getItem('cart')
        if (savedCart) {
            try {
                const cartData = JSON.parse(savedCart)
                items.value = cartData.items || []
                deliveryType.value = cartData.deliveryType || 'pickup'
                deliveryDate.value = cartData.deliveryDate || null
                deliveryTime.value = cartData.deliveryTime || null
                deliveryAddress.value = cartData.deliveryAddress || null
                customerNotes.value = cartData.customerNotes || ''
            } catch (error) {
                console.error('Error loading cart from localStorage:', error)
                clearCart()
            }
        }
    }

    // Inizializza carrello da localStorage all'avvio
    loadFromLocalStorage()

    return {
        // State
        items,
        deliveryType,
        deliveryDate,
        deliveryTime,
        deliveryAddress,
        customerNotes,
        isCheckingOut,

        // Getters
        itemCount,
        subtotal,
        deliveryFee,
        total,
        isEmpty,
        cartItemsWithDetails,

        // Actions
        addItem,
        updateQuantity,
        removeItem,
        updateCustomText,
        clearCart,
        setDeliveryInfo,
        validateCart,
        getOrderData,
        saveToLocalStorage,
        loadFromLocalStorage
    }
}, {
    persist: false // Gestione manuale con localStorage
})
